import { createContext, useContext, useState, useEffect, useCallback } from "react";
import userService from "../src/services/userService";
import i18n, { SUPPORTED_LOCALES } from "../src/i18n";
import { syncPushSubscription } from "../src/utils/push.js";

export const AuthContext = createContext();
AuthContext.displayName = "Auth";

function syncLocale(user) {
  if (user?.locale && SUPPORTED_LOCALES.includes(user.locale)) {
    i18n.changeLanguage(user.locale);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(userService.getUser());
  const [userData, setUserData] = useState(null);

  const loadUserData = useCallback(async () => {
    if (!userService.getUser()) {
      setUserData(null);
      return null;
    }
    try {
      const response = await userService.getUserData();
      setUserData(response);
      syncLocale(response?.user);
      return response;
    } catch {
      setUserData(null);
      return null;
    }
  }, []);

  useEffect(() => {
    loadUserData();
  }, [user, loadUserData]);

  // Keyed on the id, not the user object: loadUserData runs after every profile
  // and preference save, and this only needs to happen once per signed-in user.
  useEffect(() => {
    if (userData?.user?._id) syncPushSubscription();
  }, [userData?.user?._id]);

  // One write per account: the browser zone, so a Sunday reminder can skip
  // midnight–07:00 where the person actually is.
  useEffect(() => {
    const account = userData?.user;
    if (!account?._id || account.timezone) return;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timezone) return;
    userService.updateMe({ timezone }).then(() => loadUserData()).catch(() => {});
  }, [userData?.user?._id, userData?.user?.timezone, loadUserData]);

  const refreshUser = () => {
    setUser(userService.getUser());
  };

  const logIn = async (values) => {
    const response = await userService.login(values);
    refreshUser();
    return response;
  };

  const register = async (values) => {
    const response = await userService.register(values);
    refreshUser();
    return response;
  };

  const logOut = () => {
    userService.logOut();
    setUser(null);
    setUserData(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, userData, logIn, register, refreshUser, loadUserData, logOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default function useAuth() {
  return useContext(AuthContext);
}
